import * as arrow from "apache-arrow";
import initWasm, { readParquet } from "parquet-wasm";
import { parquetWorkerManager, type ParseResult } from "./parquet-worker-manager";

let wasmInitialized = false;

/**
 * Initialize the parquet-wasm WebAssembly module (for fallback)
 */
async function ensureWasmInitialized(): Promise<void> {
  if (!wasmInitialized) {
    await initWasm();
    wasmInitialized = true;
  }
}


/**
 * Synchronous fallback parsing (blocks main thread)
 * Used when Web Workers fail or aren't supported
 */
async function parseParquetFromBytesSync(
  bytes: Uint8Array
): Promise<ParseResult> {
  try {
    await ensureWasmInitialized();

    const wasmTable = readParquet(bytes);
    const arrowTable = arrow.tableFromIPC(wasmTable.intoIPCStream());
    const columns = arrowTable.schema.fields.map((field) => field.name);

    // Note: the optional post-process (e.g. SkuRow materialization) is NOT run
    // on this rare main-thread fallback — the caller re-derives it on the main
    // thread when `postProcessResult` is absent, keeping this generic path free
    // of view-specific logic.
    return { arrowTable, columns };
  } catch (error) {
    console.error("[parseParquetFromBytes] Synchronous parse failed:", error);
    throw new Error(
      `Synchronous parquet parsing failed: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Convert a single Arrow row to a JavaScript object
 * This is called on-demand when accessing rows in tables
 */
export function arrowRowToObject(
  arrowTable: arrow.Table,
  rowIndex: number,
  columns: string[]
): Record<string, unknown> {
  const rowObj = arrowTable.get(rowIndex);
  if (!rowObj) {
    return {};
  }

  const row: Record<string, unknown> = {};
  for (const col of columns) {
    row[col] = rowObj[col];
  }
  return row;
}

/**
 * Parse parquet data from a Uint8Array using Web Worker
 * Returns Arrow table instead of converting to JS objects
 * This runs in a background thread and doesn't block the main thread
 */
async function parseParquetFromBytes(
  bytes: Uint8Array,
  postProcess?: unknown
): Promise<ParseResult> {
  // Validate bytes before processing
  if (bytes.length === 0) {
    throw new Error("Parquet data is empty");
  }

  // Check for PAR1 magic bytes
  // Use slice to create a copy for validation to avoid detached buffer issues
  const validationBytes = bytes.slice(0, Math.min(20, bytes.length));
  if (
    bytes.length < 4 ||
    String.fromCharCode(
      validationBytes[0],
      validationBytes[1],
      validationBytes[2],
      validationBytes[3]
    ) !== "PAR1"
  ) {
    const firstBytes = Array.from(validationBytes)
      .map((b) => `0x${b.toString(16).padStart(2, "0")}`)
      .join(" ");
    throw new Error(
      `Invalid parquet file: expected PAR1 magic bytes, got: ${firstBytes}. ` +
        `This might indicate the base64 string contains HTML or other non-binary data.`
    );
  }

  // Try Web Worker first (non-blocking), fallback to synchronous if it fails
  const fileSizeMB = bytes.length / 1024 / 1024;
  try {
    // Create a copy of bytes to avoid detached buffer issues when transferring
    // The worker manager will transfer the buffer, which detaches the original
    const bytesCopy = new Uint8Array(bytes);
    const workerStart = performance.now();
    const result = await parquetWorkerManager.parseFromBytes(bytesCopy, postProcess);
    console.log("[PERF] [FE] parquet worker parse done", {
      workerMs: (performance.now() - workerStart).toFixed(0),
      bytes: bytes.length,
      rows: result.arrowTable.numRows,
      columns: result.columns.length,
    });
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (errorMessage.includes("timeout")) {
      // For small files (< 10MB), a timeout means the worker is stuck/broken,
      // not that the file is too large. Fall back to synchronous parsing —
      // these files parse near-instantly on the main thread.
      if (fileSizeMB < 10) {
        console.warn(
          `[parseParquetFromBytes] Worker timed out for small file (${fileSizeMB.toFixed(2)}MB), falling back to synchronous parsing`
        );
        return parseParquetFromBytesSync(bytes);
      }
      // For large files, don't block the main thread with synchronous parsing
      throw new Error(
        `Parquet parsing timed out in worker. The file may be too large (${bytes.length} bytes). ` +
          `This is non-blocking - the page should remain interactive.`
      );
    }

    // For other errors (worker creation failed, etc), use synchronous fallback
    // This ensures tables can still load even if workers fail
    console.warn(
      `[parseParquetFromBytes] Worker failed (${errorMessage}), falling back to synchronous parsing`
    );
    return parseParquetFromBytesSync(bytes);
  }
}

/**
 * Fetch parquet file from URL and parse it
 * Returns Arrow table instead of converting to JS objects
 */
export async function parseParquetFromUrl(
  url: string,
  postProcess?: unknown
): Promise<ParseResult> {
  // Handle file:// URLs - browsers can't fetch these directly
  if (url.startsWith("file://")) {
    throw new Error(
      `Cannot fetch file:// URLs in browser. ` +
        `For local development, the backend gateway should serve files via HTTP or return data URLs. ` +
        `Received file:// URL: ${url}`
    );
  }

  // Fetch the parquet file from HTTP/HTTPS URL
  const fetchStart = performance.now();
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch parquet file: ${response.status} ${response.statusText}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  console.log("[PERF] [FE] parquet fetched from URL", {
    fetchMs: (performance.now() - fetchStart).toFixed(0),
    bytes: bytes.length,
  });
  // Parse in worker (non-blocking)
  return parseParquetFromBytes(bytes, postProcess);
}

/**
 * Decode a base64-encoded parquet file and parse it.
 * Used when the VT mutation returns small parquet data inline.
 */
export async function parseParquetFromBase64(
  base64String: string,
  postProcess?: unknown
): Promise<ParseResult> {
  const binaryString = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return parseParquetFromBytes(bytes, postProcess);
}

/**
 * Parse parquet data from an S3 URL
 * Converts the S3 URL to a signed URL, fetches, and parses the parquet file
 * @param s3Url - S3 URL (s3://bucket/key) - will be converted to signed URL if getSignedUrlFn is provided
 * @param getSignedUrlFn - Optional function to convert S3 URL to signed URL
 */
export async function parseParquetFromS3(
  s3Url: string,
  getSignedUrlFn?: (s3Url: string) => Promise<string>
): Promise<{ arrowTable: arrow.Table; columns: string[] }> {
  let urlToFetch = s3Url;
  if (getSignedUrlFn && s3Url.startsWith("s3://")) {
    try {
      urlToFetch = await getSignedUrlFn(s3Url);
    } catch (error) {
      console.error("[parseParquetFromS3] Failed to get signed URL:", {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        s3Url,
      });
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "object"
            ? JSON.stringify(error)
            : String(error);
      throw new Error(`Failed to get signed URL for ${s3Url}: ${errorMessage}`);
    }
  }
  return parseParquetFromUrl(urlToFetch);
}
